import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import {
  BdResource,
  ShareableType,
  BdResources,
  BdResourceService,
} from '@bindoc-projects/resource-domain-lib';
import { BdResourceServices } from '../../resources/service/resource.service';
import { BdResourcesSelectInlineConfig } from './inline-options';
import { ReplaySubject, Subject } from 'rxjs';
import { skip } from 'rxjs/operators';
import { BD_RESOURCES_DEFAULT_SORTING } from '../../core/sorting.const';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import { BdCompanyContextService } from '@bindoc-projects/bindoc-permissions-lib/data';

export abstract class BdResourceAbstractSelectInline<
  TResource extends BdResource,
  TFormValue = TResource,
> {
  protected readonly _destroyRef = inject(DestroyRef);
  public formLoading = false;
  public optionsLoadFailed = false;
  public resourceCtrl: FormControl<TFormValue> = new FormControl<TFormValue>(
    null,
  );

  public resolvedResourceOptions: BdResourceSelectInlineOption<TResource>[];

  public resolvedConfig: BdResourcesSelectInlineConfig<TResource>;

  protected _resourceService: BdResourceService<TResource>;
  protected _resourceOptionsLoaded$: Subject<
    BdResourceSelectInlineOption<TResource>[]
  > = new ReplaySubject(1);

  private readonly _companyContextService = inject(BdCompanyContextService);
  private _companyId: string | undefined;
  private _currentResourceName: ShareableType | undefined;
  private _resourceNameInProgress = false;
  private _resourceSelectResolvingInProgress = false;

  protected constructor(
    protected readonly _resourceServices: BdResourceServices,
  ) {
    this._companyId = this._companyContextService.currentCompanyId;

    this._companyContextService.selectedCompanyId$
      .pipe(skip(1), takeUntilDestroyed(this._destroyRef))
      .subscribe((companyId) => {
        this._companyId = companyId;
        this.resolvedResourceOptions = undefined;
        if (this._currentResourceName) {
          this._onResourceNameChange(this._currentResourceName);
        }
      });
  }

  protected _onResourceNameChange(resourceName: ShareableType): void {
    this._currentResourceName = resourceName;
    this.updateResourceNameInProgress(true);
    this.optionsLoadFailed = false;
    this._resourceService = this._resourceServices.of(resourceName);

    if (!this.resolvedResourceOptions) {
      this._resourceService
        .findAll(this.resolvedConfig.minPermission, this._companyId)
        .subscribe({
          next: (resources) => {
            this.updateResourceNameInProgress(false);
            this.updateResourceOptions(resources as TResource[]);
            this._resourceOptionsLoaded$.next(this.resolvedResourceOptions);
          },
          error: (err) => {
            console.warn(
              `Laden von ${this.resolvedConfig.resourceNameTitle} nicht möglich`,
              err,
            );
            this.updateResourceNameInProgress(false);
            this.optionsLoadFailed = true;
            this.resolvedResourceOptions = [];
            this._resourceOptionsLoaded$.next(this.resolvedResourceOptions);
          },
        });
    } else {
      this.updateResourceNameInProgress(false);
      this._resourceOptionsLoaded$.next(this.resolvedResourceOptions);
    }
  }

  protected _isHtmlElementInSideSheetContainer(
    htmlElement: HTMLElement,
  ): boolean {
    if (htmlElement.tagName === 'td-side-sheet-container'.toUpperCase()) {
      return true;
    }

    if (htmlElement.parentElement) {
      return this._isHtmlElementInSideSheetContainer(htmlElement.parentElement);
    }

    return false;
  }

  protected updateResourceOptions(resources: TResource[]): void {
    this.resolvedResourceOptions = resources
      .filter((resource) => this.resolvedConfig.filterFnc(resource))
      .map((resource) => this.resourceAsInlineOption(resource as TResource))
      .sort((a, b) =>
        BdResources.sort(a.resource, b.resource, BD_RESOURCES_DEFAULT_SORTING),
      );
  }

  protected addOption(option: BdResourceSelectInlineOption<TResource>): void {
    const tmp: BdResourceSelectInlineOption<TResource>[] = [
      ...this.resolvedResourceOptions.map((it) => ({
        ...it,
        resource: it.resource ? (it.resource?.clone() as TResource) : undefined,
      })),
    ];
    tmp.push(option);

    this.resolvedResourceOptions = BdArrayUtil.unique(tmp, (it) => it.id).sort(
      (a, b) =>
        BdResources.sort(a.resource, b.resource, BD_RESOURCES_DEFAULT_SORTING),
    );
  }

  protected resourceAsAdminInlineOption(
    resource: TResource,
  ): BdResourceSelectInlineOption<TResource> {
    return this.resourceAsInlineOption(resource, ['italic']);
  }

  protected resourceAsInlineOption(
    resource: TResource,
    styleClass: string[] = undefined,
  ): BdResourceSelectInlineOption<TResource> {
    return {
      id: resource.id,
      disabled: this.resolvedConfig
        ? resource.hasError || this.resolvedConfig.disableFnc(resource)
        : resource.hasError,
      resource: resource as TResource,
      styleClass: styleClass,
    };
  }

  protected createErrorPlaceholder(
    resourceId: string,
    name: string,
    unavailableReason: BdResourceUnavailableReason,
  ): BdResourceSelectInlineOption<TResource> {
    return {
      id: resourceId,
      disabled: true,
      unavailableReason,
      resource: {
        id: resourceId,
        name,
        hasError: true,
        resourceName: undefined,
        clone: function () {
          return { ...this };
        },
      } as unknown as TResource,
      styleClass: ['italic'],
    };
  }

  protected createForbiddenPlaceholder(
    resourceId: string,
  ): BdResourceSelectInlineOption<TResource> {
    return this.createErrorPlaceholder(
      resourceId,
      'Keine Berechtigung',
      'forbidden',
    );
  }

  protected createNotFoundPlaceholder(
    resourceId: string,
  ): BdResourceSelectInlineOption<TResource> {
    return this.createErrorPlaceholder(
      resourceId,
      'Nicht vorhanden',
      'not-found',
    );
  }

  protected createRecycleBinPlaceholder(
    resourceId: string,
  ): BdResourceSelectInlineOption<TResource> {
    return this.createErrorPlaceholder(
      resourceId,
      'Im Papierkorb',
      'recycle-bin',
    );
  }

  /**
   * Builds a human-readable helper message explaining why some of the selected
   * members are shown as disabled placeholders (recycle bin / no permission /
   * deleted). Returns `null` when every selected member resolved normally.
   *
   * This is intentionally separate from the hard `optionsLoadFailed`
   * ("Laden fehlgeschlagen") error: the field is still usable, so the message
   * is informational rather than an error.
   */
  protected buildUnavailableSelectionHint(
    options: BdResourceSelectInlineOption<TResource>[],
  ): string | null {
    const reasons = options
      .map((option) => option?.unavailableReason)
      .filter((reason): reason is BdResourceUnavailableReason => !!reason);
    if (reasons.length === 0) {
      return null;
    }

    const distinctPhrases = [...new Set(reasons)].map(
      (reason) => BD_RESOURCE_UNAVAILABLE_REASON_PHRASE[reason],
    );
    const subject =
      reasons.length === 1
        ? 'Ein ausgewähltes Element ist'
        : `${reasons.length} ausgewählte Elemente sind`;

    return `${subject} nicht mehr verfügbar (${distinctPhrases.join(', ')}).`;
  }

  /**
   * Maps an "expected" resource-resolution error to a display placeholder so a
   * single unreachable member never breaks the whole selection:
   * - 403 → no permission, 404 → not found, 409 → in the user's own recycle bin.
   *
   * Returns `null` for any other error, which callers surface as a hard load
   * failure. Kept here so the single- and multiple-select variants stay in sync.
   */
  protected resolveExpectedErrorPlaceholder(
    err: unknown,
    resourceId: string,
  ): BdResourceSelectInlineOption<TResource> | null {
    if (!(err instanceof HttpErrorResponse)) {
      return null;
    }
    switch (err.status) {
      case 403:
        return this.createForbiddenPlaceholder(resourceId);
      case 404:
        return this.createNotFoundPlaceholder(resourceId);
      case 409:
        return this.createRecycleBinPlaceholder(resourceId);
      default:
        return null;
    }
  }

  protected updateResourceNameInProgress(
    resourceNameInProgress: boolean,
  ): void {
    this._resourceNameInProgress = resourceNameInProgress;
    this.formLoading =
      this._resourceNameInProgress || this._resourceSelectResolvingInProgress;
  }

  protected updateResourceSelectResolvingInProgress(
    resourceSelectResolvingInProgress: boolean,
  ): void {
    this._resourceSelectResolvingInProgress = resourceSelectResolvingInProgress;
    this.formLoading =
      this._resourceNameInProgress || this._resourceSelectResolvingInProgress;
  }
}

export interface BdResourceSelectInlineOption<TResource extends BdResource> {
  id: string;
  disabled?: boolean;
  resource?: TResource;
  styleClass?: string[];
  /**
   * Set on placeholder options that stand in for a member which could not be
   * resolved, so the select can explain the cause in a helper message.
   */
  unavailableReason?: BdResourceUnavailableReason;
}

export type BdResourceUnavailableReason =
  'recycle-bin' | 'forbidden' | 'not-found';

/** Cause phrases used to compose the "not available" helper message. */
export const BD_RESOURCE_UNAVAILABLE_REASON_PHRASE: Record<
  BdResourceUnavailableReason,
  string
> = {
  'recycle-bin': 'im Papierkorb',
  forbidden: 'ohne Berechtigung',
  'not-found': 'nicht mehr vorhanden',
};
